import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { insertClientGroupSchema, type ClientGroup } from "@shared/schema";
import { formatClientGroupName } from "@shared/format-client-group-name";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PartnerSelect } from "@/components/form-fields/partner-select";
import { z } from "zod";
import { useLocation } from "wouter";

// Extend the schema with validation rules for required fields
const clientGroupFormSchema = insertClientGroupSchema.extend({
  groupName: z.string().min(1, "Group name is required").max(255, "Group name must be less than 255 characters"),
  // Note: group_type and status were removed - not required per PRD
});

type ClientGroupFormData = z.infer<typeof clientGroupFormSchema>;

interface ClientGroupFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientGroup?: ClientGroup;
  onDelete?: () => void;
}

/**
 * Parse FK constraint error messages into plain English
 */
function parseDeleteError(errorMessage: string): string {
  const isCascadePrevention =
    errorMessage.includes("foreign key constraint") ||
    errorMessage.includes("still referenced") ||
    errorMessage.includes("violates foreign key");

  if (!isCascadePrevention) {
    return errorMessage;
  }

  // Try to extract entity counts from the error message
  // Example: "Key (id)=(xxx) is still referenced from table \"crm_x_contacts\"."
  const contactMatch = errorMessage.match(/crm_x_contacts/i);
  const dealMatch = errorMessage.match(/crm_x_deals/i);
  const accountMatch = errorMessage.match(/crm_x_accounts/i);

  const entities: string[] = [];
  if (contactMatch) entities.push('contacts');
  if (dealMatch) entities.push('deals');
  if (accountMatch) entities.push('accounts');

  if (entities.length > 0) {
    return `Cannot delete. This client group is used by ${entities.join(' and ')}.`;
  }

  return 'Cannot delete client group with linked contacts or accounts. Please remove associations first.';
}

export function ClientGroupFormModal({ open, onOpenChange, clientGroup, onDelete }: ClientGroupFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isEdit = !!clientGroup;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const form = useForm<ClientGroupFormData>({
    resolver: zodResolver(clientGroupFormSchema),
    defaultValues: {
      groupName: "",
      description: "",
      referringPartnerId: null,
    },
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (clientGroup) {
        form.reset({
          groupName: clientGroup.groupName,
          description: clientGroup.description || "",
          referringPartnerId: clientGroup.referringPartnerId || null,
        });
      } else {
        form.reset({
          groupName: "",
          description: "",
          referringPartnerId: null,
        });
      }
    }
  }, [open, clientGroup, form]);

  const createMutation = useMutation({
    mutationFn: async (data: ClientGroupFormData) => {
      const response = await apiRequest("POST", "/api/crm/client-groups", data);
      return response.json();
    },
    onSuccess: (newClientGroup: ClientGroup) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      toast({
        title: "Success",
        description: "Client group created successfully",
      });
      onOpenChange(false);
      // Navigate to the newly created client group detail page
      navigate(`/client-groups/${newClientGroup.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create client group",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ClientGroupFormData) =>
      apiRequest("PATCH", `/api/crm/client-groups/${clientGroup?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      toast({
        title: "Success",
        description: "Client group updated successfully",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update client group",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!clientGroup?.id) throw new Error("Client group ID is required");
      await apiRequest("DELETE", `/api/crm/client-groups/${clientGroup.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
      toast({
        title: "Client group deleted",
        description: "The client group has been successfully deleted.",
      });
      onOpenChange(false);
      onDelete?.();
    },
    onError: (error: Error) => {
      const friendlyMessage = parseDeleteError(error.message);

      toast({
        title: "Error",
        description: friendlyMessage || "Failed to delete client group",
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const onSubmit = (data: ClientGroupFormData) => {
    // Apply capitalization to group name before submission
    const processedData = {
      ...data,
      groupName: formatClientGroupName(data.groupName),
    };

    if (isEdit) {
      updateMutation.mutate(processedData);
    } else {
      createMutation.mutate(processedData);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Client Group" : "Add Client Group"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update the client group details" : "Create a new client group to organize your contacts and accounts"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="groupName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Family Group"
                      data-testid="input-group-name"
                      {...field}
                      onBlur={(e) => {
                        field.onBlur();
                        const capitalized = formatClientGroupName(e.target.value);
                        field.onChange(capitalized);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional notes about this client group"
                      data-testid="textarea-group-description"
                      className="resize-none"
                      rows={3}
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="referringPartnerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referring Partner</FormLabel>
                  <FormControl>
                    <PartnerSelect
                      value={field.value || null}
                      onValueChange={(value) => field.onChange(value || null)}
                      data-testid="select-referring-partner"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Danger Zone Section - RULE-SAFETY-001 */}
            {isEdit && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-between text-destructive hover:text-destructive hover:bg-destructive/10 mt-6"
                    data-testid="button-danger-zone-toggle"
                  >
                    <div className="flex items-center">
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Danger Zone
                    </div>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <Alert variant="destructive" className="border-destructive/50">
                    <AlertDescription className="text-sm">
                      Deleting this client group is permanent and cannot be undone.
                      All associated contacts and accounts will lose their group association.
                    </AlertDescription>
                  </Alert>
                  <Button
                    type="button"
                    variant="destructive"
                    className="w-full"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isPending}
                    data-testid="button-delete"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Client Group
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            )}

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"} Client Group
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="dialog-delete-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{clientGroup?.groupName}"? This action cannot be undone.
              All associated contacts and accounts will lose their group association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="button-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
